import { Card, CardContent, CardHeader, Icon, Stack, Tooltip, Typography } from '@mui/material';
import {
  AttendanceFrequencyInput,
  GET_SCHOOL_PROPERTIES_QUERY,
  useConfigureAttendanceFrequenciesMutation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useNotifications } from '@schooly/components/notifications';
import { useFlag } from '@schooly/hooks/use-flag';
import {
  Attention2Icon,
  Grid,
  GridBody,
  GridCell,
  GridContainer,
  GridHead,
  GridRow,
  InformationIcon,
  SimpleButton,
  SkeletonRows,
  Spin,
  ThreadIcon,
  WarningBlock,
} from '@schooly/style';
import { fulfillJSDWidget } from '@schooly/utils/fulfill-jsd-widget';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useSchool } from '../../../../hooks/useSchool';
import { queryClient } from '../../../../queryClient';
import { buildFrequencyConfigPayload } from '../../SchoolGeneral/SchoolGeneralAgeGroups/am-pm-helpers';
import { SchoolGeneralAgeGroupsAmPmModal } from '../../SchoolGeneral/SchoolGeneralAgeGroups/SchoolGeneralAgeGroupsAmPmModal';
import { AttendanceFrequencyRow } from './AttendanceFrequencyRow';
import { UnconfiguredAgeGroupsRow } from './UnconfiguredAgeGroupsRow';
import { useAttendanceFrequencies } from './useAttendanceFrequencies';

const COLUMNS_COUNT = 5;

// JSD help-center request name; must match an existing request in the Jira Service Desk catalog.
const SUPPORT_REQUEST = 'Ask a question';

const HelpTooltip: FC = () => {
  const { formatMessage } = useIntl();
  const { currentSchool, currentUser } = useAuth();
  const [isOpeningWidget, startOpeningWidget, endOpeningWidget] = useFlag(false);

  const openSupportWidget = async () => {
    if (isOpeningWidget) return;
    startOpeningWidget();
    await fulfillJSDWidget(SUPPORT_REQUEST, {
      summary: formatMessage({ id: 'attendance-Frequencies-SupportRequestSummary' }),
      description: currentSchool?.name,
      email: currentUser?.account_email,
    });
    endOpeningWidget();
  };

  return (
    <Tooltip
      componentsProps={{ tooltip: { sx: { maxWidth: 200, p: 1.25 } } }}
      title={
        <Stack alignItems="flex-start" gap={1.25} sx={{ p: 0.5 }}>
          <Typography>
            <FormattedMessage id="attendance-Frequencies-Help" />
          </Typography>
          <SimpleButton
            startIcon={isOpeningWidget ? <Spin /> : <ThreadIcon />}
            disabled={isOpeningWidget}
            onClick={openSupportWidget}
          >
            <Typography>
              <FormattedMessage id="attendance-Frequencies-ContactSupport" />
            </Typography>
          </SimpleButton>
        </Stack>
      }
    >
      <Stack component="span" sx={{ color: 'common.grey2', cursor: 'help' }}>
        <InformationIcon />
      </Stack>
    </Tooltip>
  );
};

export const SchoolAttendanceFrequencies: FC = () => {
  const { schoolId = '' } = useSchool();
  const { showError } = useNotifications();
  const { configuredRows, unconfiguredAgeGroups, unconfiguredLabels, isLoading } =
    useAttendanceFrequencies();
  const configureFrequencies = useConfigureAttendanceFrequenciesMutation();
  const [configuring, setConfiguring] = useState(false);

  const handleSave = (frequencyById: Record<string, AttendanceFrequencyInput>) => {
    if (!schoolId) return;

    configureFrequencies.mutate(
      { schoolId, ageGroups: buildFrequencyConfigPayload(frequencyById) },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([GET_SCHOOL_PROPERTIES_QUERY]);
          setConfiguring(false);
        },
        onError: showError,
      },
    );
  };

  return (
    <Card data-scope>
      <CardHeader
        title={
          <Stack direction="row" alignItems="center" gap={1}>
            <FormattedMessage id="attendance-Frequencies" />
            <HelpTooltip />
          </Stack>
        }
      />
      <CardContent>
        {!isLoading && unconfiguredAgeGroups.length > 0 && (
          <WarningBlock sx={{ mb: 2 }}>
            <Stack direction="row" gap={2} alignItems="center" mb={1}>
              <Icon sx={{ color: 'background.paper', path: { color: 'common.orange' } }}>
                <Attention2Icon />
              </Icon>
              <Typography variant="h3" color="inherit">
                <FormattedMessage id="attendance-Frequencies-UnconfiguredNotice" />
              </Typography>
            </Stack>
            <UnconfiguredAgeGroupsRow
              labels={unconfiguredLabels}
              onConfigure={() => setConfiguring(true)}
            />
          </WarningBlock>
        )}
        <GridContainer sx={{ borderRadius: 0 }}>
          <Grid>
            <GridHead borderBottom>
              <GridRow>
                <GridCell>
                  <Typography>
                    <FormattedMessage id="attendance-Frequencies-AgeGroups" />
                  </Typography>
                </GridCell>
                <GridCell width="200">
                  <Typography>
                    <FormattedMessage id="attendance-Frequencies-AmResponsible" />
                  </Typography>
                </GridCell>
                <GridCell width="200">
                  <Typography>
                    <FormattedMessage id="attendance-Frequencies-PmResponsible" />
                  </Typography>
                </GridCell>
                <GridCell width="150">
                  <Typography>
                    <FormattedMessage id="attendance-Frequencies-PerPeriod" />
                  </Typography>
                </GridCell>
                <GridCell width="300">
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography>
                      <FormattedMessage id="attendance-Frequencies-Timetable" />
                    </Typography>
                    <Tooltip
                      componentsProps={{ tooltip: { sx: { p: 1.25 } } }}
                      title={
                        <Typography sx={{ p: 0.5 }}>
                          <FormattedMessage id="attendance-Frequencies-TimetableHelp" />
                        </Typography>
                      }
                    >
                      <Icon sx={{ '&:hover': { color: 'primary.main' } }}>
                        <InformationIcon />
                      </Icon>
                    </Tooltip>
                  </Stack>
                </GridCell>
              </GridRow>
            </GridHead>
            <GridBody>
              {isLoading ? (
                <SkeletonRows amount={COLUMNS_COUNT} columnsCount={COLUMNS_COUNT} />
              ) : (
                configuredRows.map((row) => <AttendanceFrequencyRow key={row.key} row={row} />)
              )}
            </GridBody>
          </Grid>
        </GridContainer>
      </CardContent>

      {configuring && (
        <SchoolGeneralAgeGroupsAmPmModal
          ageGroups={unconfiguredAgeGroups.map(({ id, name }) => ({ id, name }))}
          onCancel={() => setConfiguring(false)}
          onSave={handleSave}
          saving={configureFrequencies.isLoading}
        />
      )}
    </Card>
  );
};
